// services/postEmbedService.js

import ApiService from "@/Services/ApiService";

/**
 * Extract metadata for the latest link in the given text.
 *
 * @param {string} text - The post content potentially containing a URL.
 * @returns {Promise<object|null>} - Embed metadata or null if no URL found.
 */

export async function fetchPostEmbedData(text) {
    if (typeof text !== "string") return null;

    const urlRegex = /https?:\/\/\S+/gi;
    const matchedLinks = text.match(urlRegex);
    if (!matchedLinks || matchedLinks.length === 0) return null;

    const latestLink = matchedLinks[matchedLinks.length - 1];
    try {
        const url = route("internal.scraper");
        const response = await ApiService.get(url, { url: latestLink });

        const { description, image, title } = response.data;

        // Only require title, make description and image optional
        if (!title) {
            return null;
        }
        const embed = {
            href: latestLink,
            imageUrl: image,
            youtubeImageUrl: null,
            giphyImageUrl: null,
            youtubeId: null,
            title,
            description,
            portrait: false,
            giphy: false,
            type: "external",
        };

        const youTubeIdInfo = extractYouTubeId(latestLink);
        if (youTubeIdInfo) {
            embed.youtubeId = youTubeIdInfo.youtubeId;
            embed.youtubeImageUrl = image;
            embed.portrait = youTubeIdInfo.portrait;
            embed.imageUrl = null;
            embed.type = "youtube";
        }

        const isGiphy = /(?:https?:\/\/)?(?:www\.)?giphy\.com/i.test(
            latestLink,
        );
        if (isGiphy) {
            embed.giphy = true;
            embed.giphyImageUrl = latestLink;
            embed.imageUrl = null;
            embed.type = "giphy";
        }

        return embed;
    } catch (error) {
        console.error("Error fetching embed:", error);
        return null;
    }
}

const extractYouTubeId = (url) => {
    const shortsMatch = url.match(/youtube\.com\/shorts\/([^?/]+)/);
    if (shortsMatch) {
        return { youtubeId: shortsMatch[1], portrait: true };
    }

    const videoIdMatch = url.match(/(?:\?|&)v=([^&]+)/);
    if (videoIdMatch) {
        return { youtubeId: videoIdMatch[1], portrait: false };
    }

    const youtuMatch = url.match(/youtu\.be\/([^?]+)/);
    if (youtuMatch) {
        return { youtubeId: youtuMatch[1], portrait: false };
    }

    return null;
};
